<template>
  <v-container fluid class="pa-0">
    <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

    <v-container v-if="!loading && reunion" fluid class="pa-0">
      <div class="caption grey--text mb-3">
        <router-link to="/mesa_directiva/inicio" class="grey--text">Mesa Directiva</router-link> /
        <router-link to="/mesa_directiva/reuniones" class="grey--text">Reuniones</router-link> /
        {{ reunion.titulo }}
      </div>

      <div class="d-flex justify-space-between align-center flex-wrap mb-4 gap-2">
        <div>
          <h1 class="subtitle-1 font-weight-bold mb-1">{{ reunion.titulo }}</h1>
          <p class="caption grey--text mb-0">{{ reunion.fecha }} • {{ reunion.hora }}</p>
        </div>
        <div class="d-flex align-center gap-2">
          <v-chip small :color="estadoColor(reunion.estado)" text-color="white">
            {{ reunion.estado }}
          </v-chip>
          <template v-if="reunion.estado === 'Próxima'">
            <v-btn icon small outlined color="primary" @click="abrirEditar">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small outlined color="error" @click="abrirCancelar">
              <v-icon small>mdi-close-circle</v-icon>
            </v-btn>
          </template>
        </div>
      </div>

      <v-row class="mt-2">
        <v-col cols="12" md="8">
          <v-card class="elevation-1 mb-4">
            <v-card-title>
              <v-icon color="primary" left>mdi-file-document-outline</v-icon>
              Información
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-row dense>
                <v-col cols="6">
                  <p class="overline grey--text">Fecha</p>
                  <p class="subtitle-2">{{ reunion.fecha }}</p>
                </v-col>
                <v-col cols="6">
                  <p class="overline grey--text">Hora</p>
                  <p class="subtitle-2">{{ reunion.hora }}</p>
                </v-col>
                <v-col cols="6">
                  <p class="overline grey--text">Lugar</p>
                  <p class="subtitle-2">{{ reunion.lugar }}</p>
                </v-col>
                <v-col cols="6">
                  <p class="overline grey--text">Modalidad</p>
                  <p class="subtitle-2">
                    <v-chip small :color="reunion.modalidad === 'Virtual' ? 'info' : 'success'" text-color="white">
                      <v-icon x-small left>{{ reunion.modalidad === 'Virtual' ? 'mdi-video' : 'mdi-map-marker' }}</v-icon>
                      {{ reunion.modalidad }}
                    </v-chip>
                  </p>
                </v-col>
                <v-col cols="12">
                  <p class="overline grey--text">Descripción</p>
                  <p class="subtitle-2" v-if="reunion.descripcion">{{ reunion.descripcion }}</p>
                  <p class="caption grey--text" v-else>Sin descripción</p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card class="elevation-1 mb-4">
            <v-card-title class="d-flex align-center">
              <v-icon color="primary" left>mdi-format-list-numbered</v-icon>
              Agenda
              <v-spacer></v-spacer>
              <v-chip v-if="reunion.agenda.length" small color="primary" text-color="white">
                {{ reunion.agenda.length }}
              </v-chip>
            </v-card-title>
            <v-divider></v-divider>
            <v-list v-if="reunion.agenda.length" dense>
              <v-list-item v-for="(tema, i) in reunion.agenda" :key="`tema-${i}`" class="grey lighten-5 mb-1">
                <v-list-item-icon>
                  <v-avatar size="28" color="primary lighten-3">
                    <span class="caption primary--text font-weight-bold">{{ i + 1 }}</span>
                  </v-avatar>
                </v-list-item-icon>
                <v-list-item-content class="ml-2">
                  <v-list-item-title>{{ tema }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="reunion.estado === 'Próxima'" class="flex-row">
                  <v-icon small @click="editarTema(i)" class="mr-2">mdi-pencil</v-icon>
                  <v-icon small @click="eliminarTema(i)" color="error">mdi-delete</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <v-card-text v-else class="grey--text text-center py-6">
              <v-icon large class="grey lighten-2 mb-2">mdi-clipboard-list-outline</v-icon>
              <p class="caption">Sin temas en la agenda</p>
            </v-card-text>
            <v-divider v-if="reunion.agenda.length"></v-divider>
            <v-card-actions v-if="reunion.estado === 'Próxima'">
              <v-btn small text color="primary" @click="agregarTemaDialog = true">
                <v-icon small left>mdi-plus</v-icon>
                Agregar tema
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-card class="elevation-1">
            <v-card-title>
              <v-icon color="primary" left>mdi-clipboard-check-outline</v-icon>
              Acuerdos
            </v-card-title>
            <v-divider></v-divider>
            <v-list v-if="acuerdosGenerados.length" dense>
              <v-list-item v-for="a in acuerdosGenerados" :key="a.id">
                <v-list-item-icon>
                  <v-icon small color="primary">mdi-check-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ a.titulo }}</v-list-item-title>
                  <v-list-item-subtitle class="caption">{{ a.responsable }} · {{ a.fechaLimite }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip x-small :color="estadoColor(a.estado)" text-color="white">{{ a.estado }}</v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <v-card-text v-else class="grey--text text-center py-6">
              <v-icon large class="grey lighten-2 mb-2">mdi-clipboard-outline</v-icon>
              <p class="caption">Sin acuerdos generados</p>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="elevation-1">
            <v-card-title>
              <v-icon color="primary" left>mdi-account-multiple-outline</v-icon>
              Participantes
            </v-card-title>
            <v-divider></v-divider>
            <v-card-subtitle class="pa-2">
              <v-progress-linear :value="(reunion.asistentesConfirmados / reunion.totalParticipantes * 100) || 0" color="success" class="mb-1"></v-progress-linear>
              <span class="caption">{{ reunion.asistentesConfirmados }} / {{ reunion.totalParticipantes }}</span>
            </v-card-subtitle>
            <v-list dense>
              <v-list-item v-for="(p, i) in reunion.participantes" :key="`participant-${i}`" class="mb-1">
                <v-avatar size="36" color="primary lighten-3">
                  <span class="caption primary--text font-weight-bold">{{ initials(p.name) }}</span>
                </v-avatar>
                <v-list-item-content class="ml-2">
                  <v-list-item-title class="subtitle-2">{{ p.name }}</v-list-item-title>
                  <v-list-item-subtitle class="caption">{{ p.cargo }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="reunion.estado === 'Próxima'">
                  <v-menu bottom left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip x-small v-bind="attrs" v-on="on" :color="estadoColor(p.asistencia || 'pendiente')" text-color="white">
                        {{ p.asistencia || 'Pendiente' }}
                      </v-chip>
                    </template>
                    <v-list dense>
                      <v-list-item @click="confirmarAsistencia(p, 'presente')">
                        <v-icon small left>mdi-check-circle</v-icon>
                        <span>Presente</span>
                      </v-list-item>
                      <v-list-item @click="confirmarAsistencia(p, 'ausente')">
                        <v-icon small left>mdi-close-circle</v-icon>
                        <span>Ausente</span>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-list-item-action>
                <v-list-item-action v-else>
                  <v-chip x-small :color="estadoColor(p.asistencia || 'pendiente')" text-color="white">
                    {{ p.asistencia || 'Pendiente' }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-container v-else-if="!loading" fluid>
      <v-alert type="error" prominent outlined>
        <v-icon left>mdi-alert-circle</v-icon>
        No se encontró la reunión solicitada
      </v-alert>
    </v-container>

    <!-- Diálogo para agregar/editar tema -->
    <v-dialog v-model="agregarTemaDialog" max-width="450">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ editingTemaIndex !== null ? 'Editar tema' : 'Nuevo tema' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="agregarTemaDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-text-field v-model="nuevoTema" label="Tema de agenda" outlined dense autofocus @keyup.enter="guardarTema"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="grey" @click="agregarTemaDialog = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="guardarTema" :loading="savingTema" :disabled="!nuevoTema.trim()">
            {{ editingTemaIndex !== null ? 'Actualizar' : 'Agregar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para cancelar reunión -->
    <v-dialog v-model="cancelarDialog" max-width="450">
      <v-card>
        <v-toolbar dark color="error">
          <v-toolbar-title>Cancelar reunión</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="cancelarDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <p>¿Estás seguro de cancelar esta reunión? Esta acción no se puede deshacer.</p>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="grey" @click="cancelarDialog = false">No, mantener</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="cancelarReunion" :loading="cancelingMeeting">
            Sí, cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para editar reunión -->
    <v-dialog v-model="editarDialog" max-width="600" scrollable>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Editar reunión</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="editarDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-text-field v-model="formEditar.titulo" label="Tema principal" outlined dense class="mb-3"></v-text-field>
          <v-text-field v-model="formEditar.lugar" label="Lugar / enlace" outlined dense class="mb-3"></v-text-field>
          <v-textarea v-model="formEditar.descripcion" label="Descripción" outlined dense rows="3"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="grey" @click="editarDialog = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="guardarEdicion" :loading="savingEdit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import mesaDirectivaService from '@/services/mesaDirectivaService'
import { estadoColor, initials } from './mesaDirectivaMockData'

export default {
  name: 'MesaDirectivaReunionDetalle',
  data () {
    return {
      reunion: null,
      acuerdos: [],
      loading: true,
      agregarTemaDialog: false,
      cancelarDialog: false,
      editarDialog: false,
      nuevoTema: '',
      editingTemaIndex: null,
      savingTema: false,
      cancelingMeeting: false,
      savingEdit: false,
      formEditar: {
        titulo: '',
        lugar: '',
        descripcion: ''
      }
    }
  },
  computed: {
    acuerdosGenerados () {
      if (!this.reunion) return []
      return this.acuerdos.filter(a => (this.reunion.acuerdosGeneradosIds || []).includes(a.id))
    }
  },
  async mounted () {
    await this.loadMeeting()
  },
  methods: {
    estadoColor,
    initials,
    async loadMeeting () {
      try {
        this.loading = true
        const id = this.$route.params.id
        const response = await mesaDirectivaService.getMeetingDetail(id)
        this.mapearReunion(response)
      } catch (error) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', error.message || 'Error al cargar la reunión')
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    },
    mapearReunion (reunionAPI) {
      this.reunion = {
        ...reunionAPI,
        titulo: reunionAPI.title,
        fecha: this.formatearFecha(reunionAPI.date),
        hora: reunionAPI.time,
        lugar: reunionAPI.location,
        modalidad: reunionAPI.modality,
        descripcion: reunionAPI.description,
        estado: this.mapearEstado(reunionAPI.status),
        asistentesConfirmados: reunionAPI.confirmed_attendees,
        totalParticipantes: reunionAPI.total_participants,
        participantes: reunionAPI.participants || [],
        acuerdosGeneradosIds: reunionAPI.agreements_generated_ids || [],
        agenda: reunionAPI.agenda || []
      }
    },
    mapearEstado (status) {
      const map = { upcoming: 'Próxima', finalized: 'Finalizada', cancelled: 'Cancelada' }
      return map[status] || status
    },
    formatearFecha (dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    abrirCancelar () {
      this.cancelarDialog = true
    },
    abrirEditar () {
      this.formEditar = {
        titulo: this.reunion.titulo,
        lugar: this.reunion.lugar,
        descripcion: this.reunion.descripcion
      }
      this.editarDialog = true
    },
    async cancelarReunion () {
      try {
        this.cancelingMeeting = true
        await mesaDirectivaService.cancelMeeting(this.reunion.id)
        this.reunion.estado = 'Cancelada'
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', 'Reunión cancelada correctamente')
        this.$store.commit('snackbar_activate_true')
        this.cancelarDialog = false
        setTimeout(() => this.$router.push('/mesa_directiva/reuniones'), 1500)
      } catch (error) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', error.message)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.cancelingMeeting = false
      }
    },
    async guardarEdicion () {
      try {
        this.savingEdit = true
        const payload = {
          title: this.formEditar.titulo,
          location: this.formEditar.lugar,
          description: this.formEditar.descripcion
        }
        await mesaDirectivaService.updateMeeting(this.reunion.id, payload)
        this.reunion.titulo = this.formEditar.titulo
        this.reunion.lugar = this.formEditar.lugar
        this.reunion.descripcion = this.formEditar.descripcion
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', 'Reunión actualizada correctamente')
        this.$store.commit('snackbar_activate_true')
        this.editarDialog = false
      } catch (error) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', error.message)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.savingEdit = false
      }
    },
    editarTema (index) {
      this.editingTemaIndex = index
      this.nuevoTema = this.reunion.agenda[index]
      this.agregarTemaDialog = true
    },
    async guardarTema () {
      if (!this.nuevoTema.trim()) return
      try {
        this.savingTema = true
        if (this.editingTemaIndex !== null) {
          this.reunion.agenda.splice(this.editingTemaIndex, 1, this.nuevoTema)
        } else {
          this.reunion.agenda.push(this.nuevoTema)
        }
        await this.guardarAgenda()
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', this.editingTemaIndex !== null ? 'Tema actualizado' : 'Tema agregado')
        this.$store.commit('snackbar_activate_true')
        this.agregarTemaDialog = false
        this.nuevoTema = ''
        this.editingTemaIndex = null
      } catch (error) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', error.message)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.savingTema = false
      }
    },
    async eliminarTema (index) {
      const temaBackup = this.reunion.agenda[index]
      this.reunion.agenda.splice(index, 1)
      try {
        await this.guardarAgenda()
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', 'Tema eliminado')
        this.$store.commit('snackbar_activate_true')
      } catch (error) {
        this.reunion.agenda.splice(index, 0, temaBackup)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', error.message)
        this.$store.commit('snackbar_activate_true')
      }
    },
    async guardarAgenda () {
      await mesaDirectivaService.updateMeeting(this.reunion.id, {
        agenda: this.reunion.agenda
      })
    },
    async confirmarAsistencia (participante, estado) {
      try {
        await mesaDirectivaService.confirmAttendance(this.reunion.id, {
          member_id: participante.id,
          attendance: estado
        })
        participante.asistencia = estado
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', `Asistencia: ${estado}`)
        this.$store.commit('snackbar_activate_true')
      } catch (error) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', error.message)
        this.$store.commit('snackbar_activate_true')
      }
    }
  }
}
</script>

<style scoped>
.gap-2 {
  gap: 8px !important;
}

.gap-3 {
  gap: 12px !important;
}
</style>
